import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { translations } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrency } from '../slice/selectors';
import { biType } from '../../../../locales/types';

export function QuoteItemTable(props) {
  const { displayChange, originalData, isMrc } = props;
  const rows = props.productData;
  const { t } = useTranslation();
  const currency = useSelector(selectCurrency);
  const isEqual = require('lodash.isequal');
  const [haveDifference, setHaveDifference] = useState(false);

  React.useEffect(() => {
    rows.forEach((row, index) => {
      setHaveDifference(!isEqual(row, originalData[index]));
    });
  }, [displayChange, originalData]);

  return (
    <Wrapper>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={5} align="center" className="table-header">
                {t(translations.contractDetail.table.product)}
              </TableCell>
              <TableCell colSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.chargingAccount)}
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.qty)}
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.unitPrice)}
              </TableCell>
              {props.displayType === biType.RECURRING && (
                <TableCell rowSpan={2} align="center" className="table-header">
                  {t(isMrc ? translations.contractDetail.table.recurringMRC : translations.contractDetail.table.recurringYRC)}
                </TableCell>
              )}
              {props.displayType === biType.ONEOFF && (
                <TableCell rowSpan={2} align="center" className="table-header">
                  {t(translations.contractDetail.table.nrc)}
                </TableCell>
              )}
              {props.displayType === biType.RECURRING && isMrc && (
                <TableCell rowSpan={2} align="center" className="table-header">
                  {t(translations.contractDetail.table.rentFree)}
                </TableCell>
              )}
              <TableCell rowSpan={2} align="center" className="table-header">
                %
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.floor)}
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.room)}
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.rack)}
              </TableCell>
              <TableCell rowSpan={2} align="center" className="table-header">
                {t(translations.contractDetail.table.kva)}
              </TableCell>
              {props.displayType === biType.RECURRING && (
                <TableCell rowSpan={2} align="center" className="table-header">
                  {t(translations.contractDetail.contractInformation.termDate)}
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.contractLineID)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.family)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.type)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.name)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.vasGrouping)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.code)}
              </TableCell>
              <TableCell className="table-header" align="center">
                {t(translations.contractDetail.table.description)}
              </TableCell>
            </TableRow>
          </TableHead>
          {rows.length > 0 && (
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.contractLineID}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  className={`${
                    displayChange && haveDifference && 'row-highlight-pending'
                  }`}
                >
                  <TableCell align="center">{row.contractLineID}</TableCell>
                  <TableCell align="center">{row.family}</TableCell>
                  <TableCell align="center">{row.type}</TableCell>
                  <TableCell align="center">{row.name}</TableCell>
                  <TableCell align="center">{row.vas}</TableCell>
                  <TableCell align="center">{row.accountCode}</TableCell>
                  <TableCell align="center">{row.accountDesc}</TableCell>
                  <TableCell align="center">{row.qty}</TableCell>
                  <TableCell align="center">
                    {`${currency} ${row.unitPrice.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}`}
                  </TableCell>
                  {props.displayType === biType.RECURRING && isMrc && (
                    <TableCell align="center">
                      {`${currency} ${(row.mrc??0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}`}
                    </TableCell>
                  )}
                  {props.displayType === biType.RECURRING && !isMrc && (
                    <TableCell align="center">
                      {`${currency} ${(row.yrc??0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}`}
                    </TableCell>
                  )}
                  {props.displayType === biType.ONEOFF && (
                    <TableCell align="center">
                      {`${currency} ${(row.nrc??0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}`}
                    </TableCell>
                  )}
                  {props.displayType === biType.RECURRING && isMrc && (
                    <TableCell align="center">{row.rentFreeMonth}</TableCell>
                  )}
                  <TableCell align="center">
                    {row.percentage.toLocaleString(undefined, {
                      maximumFractionDigits: 4,
                    })}
                  </TableCell>
                  <TableCell align="center">{row.floor}</TableCell>
                  <TableCell align="center">{row.room}</TableCell>
                  <TableCell align="center">{row.rack}</TableCell>
                  <TableCell align="center">{row.kva}</TableCell>
                  {props.displayType === biType.RECURRING && (
                    <TableCell align="center">{row.terminationDate}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;

  .table-header {
    background: ${p => p.theme.primary};
    color: ${p => p.theme.background};
  }

  .row-highlight-pending {
    background: ${p => p.theme.pending};
  }

  .row-highlight-complete {
    background: ${p => p.theme.complete};
  }

  .MuiTableCell-root {
    font-size: 12px;
    padding: 2px;
    min-width: 40px;
    line-height: 15px;
    border-right: 1px solid rgba(224, 224, 224, 1) !important;
  }
`;
